/**
 * 政策与地图挂接：按省/市聚合散点（与列表筛选结果一致）
 */

import type { Policy } from '../../../types';
import { resolveProvinceAdcodeLoose } from '../provinceAdcode';
import { SCATTER_ALTITUDE } from '../mapConstants';
import type { PolicyScatterDatum, ProvinceGeoJson } from '../types';
import {
    buildAdcodeToCentroidMap,
    featureLngLatByName,
    matchCityFeatureName,
} from './geoHelpers';

const COLOR_POLICY = '#40e0d0';
const COLOR_POLICY_SELECTED = '#ff7875';

function levelLabel(level: Policy['level']): string {
    if (level === 'national') return '国家级';
    if (level === 'city') return '市级';
    return '省级';
}

/** 全国图：按省级 adcode 聚合政策数量 */
export function buildPolicyScatterNational(
    policies: Policy[],
    chinaGeo: ProvinceGeoJson,
    selectedPolicyId: string | null,
): PolicyScatterDatum[] {
    const centroids = buildAdcodeToCentroidMap(chinaGeo);
    const byAd = new Map<
        string,
        { count: number; rep: Policy }
    >();

    for (const p of policies) {
        if (p.level === 'national') continue;
        const ad = resolveProvinceAdcodeLoose(p.province);
        if (!ad) continue;
        const cur = byAd.get(ad);
        if (!cur) {
            byAd.set(ad, { count: 1, rep: p });
        } else {
            cur.count += 1;
        }
    }

    const out: PolicyScatterDatum[] = [];
    for (const [ad, { count, rep }] of Array.from(byAd.entries())) {
        const ll = centroids.get(ad);
        if (!ll) continue;
        const isSel = selectedPolicyId === rep.id;
        const baseSize = Math.min(11, 6 + Math.min(count, 8) * 0.35);
        out.push({
            value: [ll[0], ll[1], SCATTER_ALTITUDE],
            name: `${rep.province} · ${count} 条`,
            policyCount: count,
            representativePolicyId: rep.id,
            levelTag: levelLabel(rep.level),
            symbolSize: isSel ? baseSize + 3 : baseSize,
            itemStyle: {
                color: isSel ? COLOR_POLICY_SELECTED : COLOR_POLICY,
                opacity: 0.92,
                borderColor: 'rgba(255,255,255,0.55)',
                borderWidth: isSel ? 2 : 1,
            },
        });
    }
    return out;
}

/** 省下钻图：按地级市名聚合（与 GeoJSON 名称对齐） */
export function buildPolicyScatterProvince(
    policies: Policy[],
    provinceGeo: ProvinceGeoJson,
    provinceAdcode: string,
    selectedPolicyId: string | null,
): PolicyScatterDatum[] {
    const byCity = new Map<string, { count: number; rep: Policy }>();

    for (const p of policies) {
        if (p.level === 'national') continue;
        const ad = resolveProvinceAdcodeLoose(p.province);
        if (ad !== provinceAdcode) continue;
        const cityKey = (p.city || p.province).trim();
        if (!cityKey) continue;
        const geoName = matchCityFeatureName(cityKey, provinceGeo) ?? cityKey;
        const cur = byCity.get(geoName);
        if (!cur) {
            byCity.set(geoName, { count: 1, rep: p });
        } else {
            cur.count += 1;
        }
    }

    const out: PolicyScatterDatum[] = [];
    for (const [geoName, { count, rep }] of Array.from(byCity.entries())) {
        const ll = featureLngLatByName(provinceGeo, geoName);
        if (!ll) continue;
        const isSel = selectedPolicyId === rep.id;
        const baseSize = Math.min(12, 6.5 + Math.min(count, 10) * 0.32);
        out.push({
            value: [ll[0], ll[1], SCATTER_ALTITUDE],
            name: `${geoName} · ${count} 条`,
            policyCount: count,
            representativePolicyId: rep.id,
            levelTag: levelLabel(rep.level),
            symbolSize: isSel ? baseSize + 3 : baseSize,
            itemStyle: {
                color: isSel ? COLOR_POLICY_SELECTED : COLOR_POLICY,
                opacity: 0.92,
                borderColor: 'rgba(255,255,255,0.55)',
                borderWidth: isSel ? 2 : 1,
            },
        });
    }
    return out;
}

export function buildPolicyScatterSeries(
    data: PolicyScatterDatum[],
): import('echarts').SeriesOption {
    return {
        type: 'scatter3D',
        name: 'policies',
        coordinateSystem: 'geo3D',
        symbol: 'diamond',
        data,
        tooltip: { show: true },
        label: { show: false, formatter: '' },
        emphasis: {
            disabled: true,
            label: { show: false, formatter: '' },
        },
        blur: { label: { show: false, formatter: '' } },
        select: { label: { show: false, formatter: '' } },
    } as unknown as import('echarts').SeriesOption;
}
